import React, { useState } from "react";
import { api } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { toast } from "sonner";
import { DownloadSimple, FileArchive } from "@phosphor-icons/react";

const ym = () => new Date().toISOString().slice(0, 7);

async function download(url, fallbackName) {
  const res = await api.get(url, { responseType: "blob" });
  const disp = res.headers["content-disposition"] || "";
  const m = disp.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : fallbackName;
  const href = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = href; a.download = name; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(href);
}

export default function ExportBackup() {
  const [month, setMonth] = useState(ym());
  const [busy, setBusy] = useState("");

  const exportTally = async () => {
    setBusy("tally");
    try { await download(`/export/tally?month=${month}`, `tally-export-${month}.xml`); toast.success("Tally XML downloaded"); }
    catch { toast.error("Export failed"); } finally { setBusy(""); }
  };
  const backup = async () => {
    setBusy("backup");
    try { await download("/export/backup", "somcore-backup.json"); toast.success("Backup downloaded"); }
    catch { toast.error("Backup failed"); } finally { setBusy(""); }
  };

  return (
    <Layout>
      <PageHeader title="Export & Backup" subtitle="Export to Tally or take a full data backup" />
      <div className="p-8" data-testid="export-page">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-sm border border-gray-200 bg-white p-6">
            <div className="mb-2 flex items-center gap-2"><FileArchive size={20} className="text-[#1B4383]" /><span className="font-display text-lg font-bold text-gray-900">Tally Export</span></div>
            <p className="mb-4 text-sm text-gray-500">Generates a Tally-importable XML for the selected month: all ledger masters (chart of accounts + suppliers + clients) and every journal voucher. In Tally: <em>Gateway → Import Data → Vouchers/Masters</em>.</p>
            <div className="flex items-end gap-3">
              <div><Label className="lx-label">Month</Label><Input type="month" className="mt-1 w-48" data-testid="export-month" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
              <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="export-tally-btn" disabled={busy === "tally"} onClick={exportTally}>
                <DownloadSimple size={16} className="mr-1" />{busy === "tally" ? "Preparing…" : "Download Tally XML"}
              </Button>
            </div>
          </div>

          <div className="rounded-sm border border-gray-200 bg-white p-6">
            <div className="mb-2 flex items-center gap-2"><DownloadSimple size={20} className="text-gray-700" /><span className="font-display text-lg font-bold text-gray-900">Full Data Backup</span></div>
            <p className="mb-4 text-sm text-gray-500">Downloads a complete JSON snapshot of all core data (transactions, ledger, parties, payments, invoices, inventory and more) for safekeeping.</p>
            <Button variant="outline" data-testid="export-backup-btn" disabled={busy === "backup"} onClick={backup}>
              <DownloadSimple size={16} className="mr-1" />{busy === "backup" ? "Preparing…" : "Download Backup (JSON)"}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
